import { FC } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

/** Minimal file shape the hub tiles/preview need (satisfied by DocFile). */
export interface HubFile {
  id: number;
  name: string;
  pdf_file_url: string;
  is_rejected?: boolean;
  /** Question (QIT) this file belongs to, used to deep-link to edit it. */
  qitId?: number;
}

interface HubFileTileProps {
  file: HubFile;
  onClick: (file: HubFile) => void;
}

const HubFileTile: FC<HubFileTileProps> = ({ file, onClick }) => {
  const theme = useTheme();
  const rejected = file.is_rejected === true;
  return (
    <Stack
      role="button"
      tabIndex={0}
      aria-label={`Preview ${file.name}`}
      onClick={() => onClick(file)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick(file);
        }
      }}
      sx={{
        bgcolor: 'background.paper',
        borderRadius: '12px',
        boxShadow: '0px 1px 6px rgba(0,0,0,0.03)',
        p: '12px 10px',
        gap: 1,
        cursor: 'pointer',
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          height: 52,
          borderRadius: '8px',
          backgroundColor: alpha(
            rejected ? theme.palette.warning.main : theme.palette.error.main,
            0.12
          ),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {rejected ? (
          <ReportProblemOutlinedIcon
            sx={{ fontSize: 24, color: 'warning.dark' }}
          />
        ) : (
          <InsertDriveFileOutlinedIcon
            sx={{ fontSize: 24, color: 'error.main' }}
          />
        )}
      </Box>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 500,
          lineHeight: 1.3,
          wordBreak: 'break-word',
        }}
      >
        {file.name}
      </Typography>
      {rejected && (
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            lineHeight: 1.3,
            color: 'warning.dark',
          }}
        >
          Rejected — please re-upload
        </Typography>
      )}
    </Stack>
  );
};

export default HubFileTile;
